import React, { Dispatch, useEffect, useMemo, useRef, useState } from "react";
import { CircularProgress, Stack, Typography } from "@mui/material";
import FormattedMessageJamezz from "../../../global/components/FormattedMessageJamezz";
import useArticleSearchFiltering from "../../../global/utils/menu/filters/useArticleSearchFiltering";

import Article from "../../../global/utils/models/menu/Article";
import Box from "@mui/material/Box";
import { debounce } from "lodash";
import { useAppSelector } from "../../../global/utils/redux/store";
import { selectArticlesSum } from "../../../global/utils/redux/shoppingCartSlice";
import { selectArticlesMap } from "../../../global/utils/redux/selectors/selectArticlesMap";
import LazyLoadItems from "../../../global/components/LazyLoadItems";
import ArticleContent from "./ArticleContent.tsx";

const debounceSearch = debounce(
  (
    setFilteredArticles: Dispatch<Article[]>,
    searchArticles: ReturnType<typeof useArticleSearchFiltering>,
    setIsSearching: (isSearching: boolean) => void
  ) => {
    setFilteredArticles(searchArticles());
    setIsSearching(false);
  },
  100,
  { leading: true }
);

export default function SearchContent(props: { pixelHeightCartButton: string }) {
  const [isSearching, setIsSearching] = useState(false);
  const articlesMap = useAppSelector(selectArticlesMap);

  const articles = useMemo<Article[]>(() => {
    return Object.values(articlesMap).filter((a): a is Article => Boolean(a));
  }, [articlesMap]);

  const [filteredArticles, setFilteredArticles] = useState<Article[] | null>(null);

  const searchArticles = useArticleSearchFiltering(articles);
  const articleSearchText = useAppSelector((state) => state.global.articleSearchText);
  useEffect(() => {
    setIsSearching(true);
    debounceSearch(setFilteredArticles, searchArticles, setIsSearching);
  }, [searchArticles, articleSearchText]);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const count = useAppSelector(selectArticlesSum);

  const LazyLoad = useMemo(() => {
    return (
      <LazyLoadItems
        items={filteredArticles ?? []}
        numberOfItemsToRenderInitially={50}
        numberOfItemsToAddEachLoad={10}
        scrollContent={contentRef}
        pixelHeightCartButton={count > 0 ? props.pixelHeightCartButton : undefined}
        articleContent={ArticleContent}
        sizeOfCircularProgress={32}
      />
    );
  }, [count, filteredArticles, props.pixelHeightCartButton]);

  return (
    <Box
      className={"JS-SearchContent-Root"}
      ref={contentRef}
      sx={{
        position: "relative",
        alignContent: "flex-start",
        flex: 1,
        overflowY: "auto",
      }}
    >
      {filteredArticles != null ? (
        <>
          {filteredArticles.length > 0 ? (
            <Stack sx={{ margin: 4 }} direction={"row"} alignItems={"center"} gap={4}>
              <Typography
                className={"JS-SearchContent-Name"}
                sx={{
                  fontWeight: 800,
                  fontStyle: "italic",
                  color: "primary.main",
                }}
              >
                <FormattedMessageJamezz id={"Search.title.results"} />
              </Typography>
              {isSearching ? <CircularProgress size={32} /> : null}
            </Stack>
          ) : null}
          {filteredArticles.length === 0 ? (
            <Box className={"JS-ArticleContent-Root"} sx={{ display: "flex", flexDirection: "column", margin: 4 }}>
              <Typography sx={{ fontStyle: "italic", color: "primary.main" }} data-cy="search-no-articles-found">
                <FormattedMessageJamezz id={"No articles found..."} />
              </Typography>
            </Box>
          ) : null}

          {LazyLoad}
        </>
      ) : (
        <CircularProgress size={32} />
      )}
    </Box>
  );
}
